import { useEffect, useLayoutEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { holdLoader, releaseLoader } from "../../../store/mainSlice";
import {
  selectCanPurchaseChecked,
  selectPassesSettled,
} from "../../../store/multipleEntryPassSlice";
import { isPartnerClalit, isPartnerMaoflaamit, isPartnerOvdimBari } from "../../../store/partnerStore";
import { selectCarouselsFetchedFor } from "../../store/homeCarouselsSlice";
import { HOME_CAROUSELS } from "./carouselTypes";

// All rows settle atomically — a single GetCarousels call feeds every one — so
// the gate simply waits for that round.
const GATE_ROWS = HOME_CAROUSELS.map(({ key }) => key);

// Hanging requests must not hold a full-screen overlay indefinitely; past this
// the skeletons take over. The window is anchored at the moment the carousel
// round actually fires (credentials only land after the handshake, so a
// mount-anchored window used to expire before the requests even started) — with
// mount as the fallback anchor, so a handshake that never delivers credentials
// still can't wedge the overlay forever.
const CAP_MS = 8000;

// This hook's key into mainSlice.loaderHolds. A static key is safe here (unlike
// the handshake's): there are no async continuations, and route-switch ordering
// runs the old instance's cleanup before the new instance's effects.
const HOLD_KEY = "homeGate";

/**
 * The global overlay for a *cold* home load — the case where the data fires at
 * navigation time (fresh GPS just granted, nothing prefetched) and the user would
 * otherwise stare at shimmer for the length of the requests.
 *
 * The gate waits on everything the user sees on arrival: the carousel rows
 * (one request, they settle together), the passes (GetAllFreefitWebPass) and the purchase-eligibility
 * check (CanPassPurchasePossible) that drive the pass banner. The pass pair only
 * counts for the partners that actually fire it (see screens/mainPage.jsx) —
 * elsewhere it is vacuously settled, so a rollout to a new partner can't wedge.
 * The handshake itself holds the overlay under its own key from LayoutV2, so
 * this gate never needs to model it.
 *
 * On the warm path — welcome prefetched and everything settled before home
 * mounted — the gate is already open on the first render and nothing ever shows.
 *
 * Once released, never re-raises (the latch): the >100m revalidate swaps rows in
 * place later and must not flash an overlay over a populated page, and a return
 * visit on the session cache must stay silent.
 *
 * @param {Object} rows - homeCarouselsSlice.rows, as useHomeCarousels returns them
 */
export default function useHomeLoader(rows) {
  const dispatch = useDispatch();
  const isClalit = useSelector(isPartnerClalit);
  const isOvdimBari = useSelector(isPartnerOvdimBari);
  const isMaoflaamit = useSelector(isPartnerMaoflaamit);
  const passesSettled = useSelector(selectPassesSettled);
  const canPurchaseChecked = useSelector(selectCanPurchaseChecked);
  // Flips off null the moment the carousel round is dispatched — the cap anchor.
  const fetchStarted = useSelector(selectCarouselsFetchedFor) !== null;

  // "error" counts as settled everywhere below: a failed row/banner hides itself,
  // it must not wedge the gate.
  const rowsSettled = GATE_ROWS.every((key) => (rows[key]?.status ?? "loading") !== "loading");
  const passPartner = isClalit || isOvdimBari || isMaoflaamit;
  const passesGateOpen = !passPartner || (passesSettled && canPurchaseChecked);
  const gateOpen = rowsSettled && passesGateOpen;

  const releasedRef = useRef(false);
  const raisedRef = useRef(false);

  // Lower only what this hook raised, so a release here can't clear another
  // owner's hold — and a repeat call is a no-op.
  const release = () => {
    releasedRef.current = true;
    if (raisedRef.current) {
      raisedRef.current = false;
      dispatch(releaseLoader(HOLD_KEY));
    }
  };

  // Layout effect, not passive: on a cold mount the overlay has to be up before the
  // first paint, or the skeletons flash for a frame underneath it.
  useLayoutEffect(() => {
    if (releasedRef.current) return;
    if (gateOpen) {
      // Warm cache — mark released so nothing here ever runs again.
      releasedRef.current = true;
      return;
    }
    raisedRef.current = true;
    dispatch(holdLoader(HOLD_KEY));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Release when everything on the fold settles.
  useEffect(() => {
    if (releasedRef.current || !gateOpen) return;
    release();
    // release is stable enough (refs + dispatch); listing it would re-run on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateOpen, dispatch]);

  // ...or when the cap expires, whichever comes first. The window opens at mount
  // (the never-got-credentials fallback) and re-opens once when fetchStarted flips
  // true — a fresh CAP_MS from the moment the round actually fired. The latch
  // makes a later flip back (retry via carouselsInvalidated) a no-op.
  useEffect(() => {
    if (releasedRef.current) return;
    const timer = setTimeout(release, CAP_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchStarted, dispatch]);

  // Unmount mid-load: lower only what this hook raised, so navigating away
  // can't strand the overlay — and can't clear someone else's either.
  useEffect(
    () => () => {
      if (raisedRef.current) {
        raisedRef.current = false;
        dispatch(releaseLoader(HOLD_KEY));
      }
    },
    [dispatch],
  );
}
